/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useRef, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import {IconSize} from '@ion/dls/src/types/types';
import SentinelRawMessage from './SentinelRawMessage';

interface SentinelRowEditorProps {
  data?: GridResult;
};

/**
 * Provides a menu for a Sentinel event row, allowing users to view the raw
 * event message. Integrates with the `CellMenu` component for row-level
 * actions in a grid.
 *
 * ```
 * 
 * <SentinelRowEditor data={eventData} />
 * 
 * ```
 * 
 */
const SentinelRowEditor = ({
  data
}: SentinelRowEditorProps) => {
  const cellMenuRef                         = useRef(null);
  const [viewRawMessage, setViewRawMessage] = useState(false);

  const closeMenu = function() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const handleRawMessage = function() {
    closeMenu();
    setViewRawMessage(true);
  };

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div>
          <CellMenuItem icon={<ViewRecordIcon size={IconSize.medium} />}
            onClick={handleRawMessage}
            label={translate('common:view')} />
        </div>
      } />
      <SentinelRawMessage data={data}
        open={viewRawMessage}
        handleRawMessage={setViewRawMessage} />
    </>
  );
};

export default React.memo(SentinelRowEditor);
